/**
 * Navbar — yuqori panel (AvtoFix Design System)
 *
 * Spec:
 *   - Height: 56px (lg+) / 52px (sm)
 *   - bg-card, border-bottom, sticky top
 *   - Mobile: menyu tugmasi + brand
 *   - Desktop: search field (Ctrl+K hint) + actions
 *   - Action group: OfflineIndicator · Bell · ThemeToggle · Profile dropdown
 *   - Profile dropdown — avatar + ism, bosilganda menyu (Sozlamalar, Tema, Chiqish)
 */

import { Bell, LogOut, Menu, Search, User } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ConfirmDialog } from '@/components/confirm-dialog';
import { StylePicker } from '@/components/style-picker';
import { ThemeToggle } from '@/components/theme-toggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCurrentUser, useLogout } from '@/features/auth/auth.hooks';
import { OfflineIndicator } from '@/features/offline/OfflineIndicator';
import { cn } from '@/shared/lib/cn';

import { StoreSwitcher } from './StoreSwitcher';

const ROLE_LABEL: Record<string, string> = {
  EGASI: 'Egasi',
  ADMIN: 'Admin',
  XODIM: 'Xodim',
};

const ROLE_TONE: Record<string, string> = {
  EGASI: 'bg-primary/10 text-primary',
  ADMIN: 'bg-info/10 text-info',
  XODIM: 'bg-success/10 text-success',
};

export function Navbar({
  onMobileMenuToggle,
}: {
  onMobileMenuToggle?: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const logout = useLogout();
  const user = useCurrentUser();
  const [logoutOpen, setLogoutOpen] = useState(false);

  const userInitial = user?.name.charAt(0).toUpperCase() ?? '?';
  const roleLabel = ROLE_LABEL[user?.role ?? ''] ?? user?.role ?? '';
  const roleTone = ROLE_TONE[user?.role ?? ''] ?? 'bg-muted text-muted-foreground';

  return (
    <header className="z-navbar sticky top-0 flex min-h-14 shrink-0 items-center gap-3 border-b border-border bg-card px-4 pt-[env(safe-area-inset-top)] sm:px-5">
      {/* Mobile menu */}
      {onMobileMenuToggle !== undefined && (
        <button
          type="button"
          onClick={onMobileMenuToggle}
          className="flex size-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground lg:hidden"
          aria-label="Menyu"
        >
          <Menu className="size-[18px]" strokeWidth={1.75} />
        </button>
      )}
      <div className="flex items-center gap-2 lg:hidden">
        <span className="text-base font-extrabold tracking-tight text-foreground">
          Avto<span className="text-primary">Fix</span>
        </span>
      </div>

      {/* Search (desktop) */}
      <div className="relative hidden max-w-[480px] flex-1 lg:block">
        <Search
          className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          strokeWidth={2}
          aria-hidden
        />
        <input
          type="search"
          placeholder="Mahsulot, mijoz, buyurtma qidirish..."
          className={cn(
            'h-10 w-full rounded-lg border border-border bg-muted/30 pl-10 pr-16 text-sm font-medium',
            'text-foreground placeholder:text-muted-foreground',
            'transition-all',
            'focus:border-primary focus:bg-card focus:outline-none focus:ring-2 focus:ring-primary/20',
          )}
          aria-label="Qidirish"
        />
        <kbd
          aria-hidden
          className="absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-border bg-card px-1.5 py-0.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex"
        >
          Ctrl K
        </kbd>
      </div>

      {/* Spacer — actions guruhi har doim o'ng burchakda turishi uchun */}
      <div className="flex-1" />

      {/* Actions — burchakda */}
      <div className="flex shrink-0 items-center gap-1">
        {/* Joriy do'kon tanlagich — faqat EGASI ko'radi (do'konlar aralashmasin) */}
        <StoreSwitcher />

        <OfflineIndicator />

        <NavIconButton ariaLabel="Bildirishnomalar" title="Bildirishnomalar">
          <Bell className="size-[18px]" strokeWidth={1.75} />
          {/* Optional notification badge */}
          {/* <span className="absolute right-1 top-1 size-1.5 rounded-full bg-destructive" /> */}
        </NavIconButton>

        <ThemeToggle />

        <span className="mx-1 hidden h-6 w-px bg-border sm:block" aria-hidden />

        {/* Profile dropdown — burchakda kompakt avatar (faqat ikon) */}
        {user !== null ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  'group relative flex shrink-0 items-center justify-center rounded-full',
                  'size-9 transition-all hover:ring-2 hover:ring-primary/30 hover:ring-offset-2 hover:ring-offset-card',
                  'data-[state=open]:ring-2 data-[state=open]:ring-primary data-[state=open]:ring-offset-2 data-[state=open]:ring-offset-card',
                )}
                aria-label={`Foydalanuvchi profili: ${user.name}`}
                title={`${user.name} (${roleLabel})`}
              >
                <span
                  className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-full',
                    'bg-gradient-to-br from-primary to-primary/70 text-sm font-bold text-primary-foreground shadow-sm',
                  )}
                >
                  {userInitial}
                </span>
                {/* Status dot — online indicator (yashil nuqta) */}
                <span
                  aria-hidden
                  className="absolute bottom-0 right-0 size-2.5 rounded-full border-2 border-card bg-success"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>
                <div className="flex items-center gap-3 py-1">
                  <span
                    className={cn(
                      'flex size-10 shrink-0 items-center justify-center rounded-full',
                      'bg-gradient-to-br from-primary to-primary/70 text-base font-bold text-primary-foreground shadow-sm',
                    )}
                  >
                    {userInitial}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-1 text-sm font-semibold text-foreground">
                      {user.name}
                    </p>
                    <p className="line-clamp-1 text-xs text-muted-foreground">@{user.username}</p>
                    <span
                      className={cn(
                        'mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                        roleTone,
                      )}
                    >
                      {roleLabel}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <div
                className="px-2 py-1.5"
                onClick={(e) => {
                  e.preventDefault();
                }}
              >
                <p className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Dizayn
                </p>
                <StylePicker compact />
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/settings" className="cursor-pointer">
                  <User className="size-4" />
                  Profil
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onClick={() => {
                  setLogoutOpen(true);
                }}
              >
                <LogOut className="size-4" />
                {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <NavIconButton
            ariaLabel={t('nav.logout')}
            title={t('nav.logout')}
            tone="destructive"
            onClick={() => {
              setLogoutOpen(true);
            }}
          >
            <LogOut className="size-[18px]" strokeWidth={1.75} />
          </NavIconButton>
        )}

        <ConfirmDialog
          open={logoutOpen}
          onOpenChange={setLogoutOpen}
          title="Tizimdan chiqish"
          description="Tizimdan chiqmoqchimisiz?"
          confirmLabel="Ha, chiqish"
          onConfirm={() => {
            setLogoutOpen(false);
            logout();
          }}
        />
      </div>
    </header>
  );
}

/**
 * NavIconButton — 36×36 ghost icon tugma (Design: navbar action standart).
 * tone="destructive" → matn qizil, hover'da soft red fon.
 */
function NavIconButton({
  children,
  ariaLabel,
  title,
  onClick,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  ariaLabel: string;
  title?: string;
  onClick?: () => void;
  tone?: 'neutral' | 'destructive';
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative flex size-9 items-center justify-center rounded-md transition-colors duration-150',
        tone === 'destructive'
          ? 'hover:bg-destructive-soft text-destructive'
          : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
      aria-label={ariaLabel}
      title={title}
    >
      {children}
    </button>
  );
}
